import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Globe } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

export default function LoginPage() {
  const { login, user } = useAuth();
  const { t, toggle, dir } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@mnah.sa");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const isRtl = dir === "rtl";

  useEffect(() => {
    if (user && user !== false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome");
      navigate("/admin");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="login-page">
      <div className="flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <div className="flex items-center justify-between">
            <Link to="/" data-testid="login-back-home">
              <Logo />
            </Link>
            <Button variant="ghost" size="sm" onClick={toggle} className="gap-1.5" data-testid="login-lang-toggle">
              <Globe className="h-4 w-4" />
              <span className="font-semibold">{t.other.label}</span>
            </Button>
          </div>
          <div className="mt-12">
            <span className="overline">{t.login.eyebrow}</span>
            <h1 className="mt-4 text-4xl sm:text-5xl font-black leading-[1.1]">
              {t.login.heading1} <br />
              <span className="text-primary">{t.login.heading1Accent}</span>
            </h1>
            <p className="mt-4 text-muted-foreground">{t.login.description}</p>
          </div>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] mb-2 block">{t.login.labelEmail}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@mnah.sa"
                className="rounded-sm h-12"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] mb-2 block">{t.login.labelPassword}</Label>
              <div className="relative">
                <Input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`rounded-sm h-12 ${isRtl ? "pl-12" : "pr-12"}`}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className={`absolute top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground ${isRtl ? "left-3" : "right-3"}`}
                  data-testid="login-password-toggle"
                  aria-label="Toggle password visibility"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full h-12 rounded-sm bg-secondary hover:bg-primary text-secondary-foreground hover:text-primary-foreground transition-colors"
              data-testid="login-submit-btn"
            >
              {loading ? t.login.submitting : (
                <>
                  {t.login.submit} <ArrowRight className={`h-4 w-4 ${isRtl ? "mr-2 scale-x-[-1]" : "ml-2"}`} />
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 text-xs text-muted-foreground border border-dashed border-border p-4">
            <span className="font-semibold text-foreground">{t.login.demoLabel}</span> admin@mnah.sa /
            MnahAdmin@2026
          </div>
        </motion.div>
      </div>

      <div className="hidden lg:block relative bg-secondary overflow-hidden grain">
        <img
          src="https://images.unsplash.com/photo-1673498369434-0086d17874b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="Pattern"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/95 via-secondary/70 to-transparent" />
        <div className="absolute inset-0 p-16 flex flex-col justify-end text-secondary-foreground">
          <span className="overline text-primary">{t.login.right.eyebrow}</span>
          <h2 className="mt-6 text-5xl xl:text-7xl font-black leading-[0.95]">
            {t.login.right.h1} <br /> <span className="text-primary">{t.login.right.accent}</span> <br /> {t.login.right.h2}
          </h2>
          <p className="mt-6 max-w-md text-secondary-foreground/70 leading-relaxed">
            {t.login.right.description}
          </p>
        </div>
      </div>
    </div>
  );
}
